import { useState, useEffect, useMemo } from 'react';
import { makeStyles, Button, ButtonGroup, Typography, Box, Chip } from '@material-ui/core';
import { ViewList, AccountTree } from '@material-ui/icons';
import '@xyflow/react/dist/style.css';
import { getSpecificationById } from '../data/index';
import { getSurveyMetadata, convertToTableFormat } from './utils/dataConverters';
import SurveyFlow from './components/SurveyFlow';

const useStyles = makeStyles((theme) => ({
  '@keyframes pulse': {
    '0%': {
      backgroundColor: '#fef3c7',
    },
    '50%': {
      backgroundColor: '#fde68a',
    },
    '100%': {
      backgroundColor: '#fef3c7',
    },
  },
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
  },
  surveyTitle: {
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: theme.spacing(0.5),
  },
  surveyMeta: {
    color: '#64748b',
    fontSize: '0.9rem',
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  viewToggle: {
    background: '#f8fafc',
    borderRadius: '12px',
    padding: '4px',
    border: '1px solid #e2e8f0',
    '& .MuiButtonGroup-root': {
      boxShadow: 'none',
    },
    '& .MuiButton-root': {
      minWidth: 80,
      padding: theme.spacing(1, 2),
      borderRadius: '8px',
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
      border: 'none !important',
      transition: 'all 0.2s ease-in-out',
      '&.MuiButton-containedPrimary': {
        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%) !important',
        color: 'white !important',
        boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%) !important',
          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
        },
      },
      '&.MuiButton-contained:not(.MuiButton-containedPrimary)': {
        background: '#e2e8f0 !important',
        color: '#64748b !important',
        '&:hover': {
          background: '#cbd5e1 !important',
          color: '#475569 !important',
        },
      },
    },
    '& .MuiButton-startIcon': {
      marginRight: theme.spacing(0.5),
      '& .MuiSvgIcon-root': {
        fontSize: '18px',
      },
    },
  },
  contentArea: {
    flex: 1,
    minHeight: 600,
    background: 'white',
    borderRadius: 12,
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
  },

  // Table styles
  tableContainer: {
    height: '100%',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    '& th': {
      background: '#f8fafc',
      padding: theme.spacing(1.5),
      textAlign: 'left',
      fontWeight: 600,
      color: '#374151',
      borderBottom: '2px solid #e5e7eb',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
    '& td': {
      padding: theme.spacing(1),
      borderBottom: '1px solid #f3f4f6',
      verticalAlign: 'middle',
    },
    '& td:first-child': {
      backgroundColor: 'transparent'
    },
  },
  questionCell: {
    maxWidth: 300,
    fontWeight: 500,
    color: '#1e293b',
  },
  optionsCell: {
    maxWidth: 250,
    fontSize: '0.85rem',
  },
  showIfCell: {
    maxWidth: 200,
    fontSize: '0.8rem',
    fontFamily: 'monospace',
  },
  automationCell: {
    textAlign: 'center',
    minWidth: 100,
  },
  typeChip: {
    fontSize: '0.75rem',
    height: 20,
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
  },
  nestedQuestion: {
    paddingLeft: 20,
    fontStyle: 'italic',
    color: '#64748b',
  },
  groupRow: {
    backgroundColor: '#f9f9f9 !important',
  },
  gridRow: {
    backgroundColor: '#fafafa !important',
  },
  requiredIndicator: {
    color: '#059669',
    fontWeight: 'bold',
  },
  viewDetailsButton: {
    fontSize: '0.75rem',
    padding: '4px 8px',
    minWidth: 'auto',
  },
  highlightedRow: {
    animation: '$pulse 3s ease-in-out',
  },

  // Flow view styles
  flowContainer: {
    height: '100%',
    position: 'relative',
    '& .react-flow': {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderRadius: 12,
      border: '1px solid #e2e8f0',
    },
    '& .react-flow__controls': {
      background: 'rgba(255, 255, 255, 0.9)',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
    },
    '& .react-flow__minimap': {
      background: 'white',
      border: '1px solid #e2e8f0',
      borderRadius: 8,
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    },
    '& .react-flow__edge-text': {
      fontSize: '11px',
      fontWeight: 'bold',
      background: 'rgba(255, 255, 255, 0.95)',
      padding: '4px 8px',
      borderRadius: '6px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      whiteSpace: 'pre-line',
      textAlign: 'center',
    },
    '& .react-flow__edge-path': {
      filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))',
    },
  },

  // Custom node styles
  questionNode: {
    padding: theme.spacing(1.5),
    background: 'white',
    border: '2px solid #3b82f6',
    borderRadius: 8,
    minWidth: 200,
    maxWidth: 250,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  conditionNode: {
    padding: theme.spacing(2),
    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
    border: '3px solid #f59e0b',
    borderRadius: 16,
    minWidth: 160,
    maxWidth: 220,
    boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -2,
      left: -2,
      right: -2,
      bottom: -2,
      background: 'linear-gradient(45deg, #f59e0b, #fbbf24)',
      borderRadius: 18,
      zIndex: -1,
    },
  },

  // Automation table styles
  automationSection: {
    marginTop: theme.spacing(3),
  },
  automationTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    color: '#1e293b',
  },
  automationTable: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
    '& th': {
      background: '#f8fafc',
      padding: theme.spacing(1.5),
      textAlign: 'left',
      fontWeight: 600,
      color: '#374151',
      borderBottom: '2px solid #e5e7eb',
    },
    '& td': {
      padding: theme.spacing(1.5),
      borderBottom: '1px solid #f3f4f6',
      verticalAlign: 'top',
    },
  },
  automationHighlighted: {
    animation: '$pulse 3s ease-in-out',
  },
}));

const SurveySpecificationPage = () => {
  const classes = useStyles();
  const [currentView, setCurrentView] = useState('table');
  const [highlightedQuestionId, setHighlightedQuestionId] = useState(null);
  const [surveyData, setSurveyData] = useState(null);
  const [metadata, setMetadata] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSpecification = async () => {
      try {
        setLoading(true);
        // Always load pediatric-health-assessment for now
        const specification = getSpecificationById('pediatric-health-assessment');

        if (specification) {
          setSurveyData(specification.data);
          setMetadata(getSurveyMetadata(specification.data));

          // Debug: log automation rules
          console.log('Loaded survey data with automation rules:', specification.data.surveyAutomationRules?.length || 0);
        }
      } catch (err) {
        console.error('Error loading specification:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSpecification();
  }, []);

  // Convert data for table display
  const tableData = useMemo(() => {
    if (!surveyData) return [];
    return convertToTableFormat(surveyData);
  }, [surveyData]);

  // Render colored conditional expression (like in original implementation)
  const renderShowIfExpression = (expression) => {
    if (!expression) return null;

    return (
      <div style={{
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        lineHeight: '1.4',
        wordBreak: 'break-word'
      }}>
        {expression.split(' ').map((word, index) => {
          const isOR = word === 'OR' || word.toLowerCase() === 'or';
          const isAND = word === 'AND' || word.toLowerCase() === 'and';
          const isQuestionRef = /^\[[\d.]+\]$/.test(word);
          const isLetter = /^[A-Z]\)?$/.test(word);
          const isOperator = /^(=|<=|>=|<|>)$/.test(word);

          let style = {};

          if (isOR || isAND) {
            style = { color: '#FF9800', fontWeight: 'bold' };
          } else if (isQuestionRef) {
            style = { color: '#2196F3', fontWeight: 'bold' };
          } else if (isLetter) {
            style = { color: '#4CAF50', fontWeight: 'bold' };
          } else if (isOperator) {
            style = { color: '#FF9800', fontWeight: 'bold' };
          }

          return (
            <span key={index} style={style}>
              {isOR || isAND ? word.toLowerCase() : word}{index < expression.split(' ').length - 1 ? ' ' : ''}
            </span>
          );
        })}
      </div>
    );
  };

  // Check if question should inherit group condition
  const getShowIfText = (question) => {
    const isChildOfConditionalGroup = question.id === 'q22' ||
                                    question.id === 'q21.0' ||
                                    (question.id && question.id.match(/^q21\.[1-9]$/)) ||
                                    question.id === 'q28_grid' ||
                                    (question.id && question.id.match(/^q28\.[1-7]$/));

    if (isChildOfConditionalGroup) {
      if (question.id === 'q22' || (question.id && question.id.match(/^q21\.[1-9]$/))) {
        return "Per group Q21 condition";
      }
      if (question.id === 'q28_grid' || (question.id && question.id.match(/^q28\.[1-7]$/))) {
        return "Per group Q28 condition";
      }
    }

    return question.showIf || '';
  };

  // Get automation count for question
  const getAutomationCount = (questionId) => {
    if (!surveyData?.surveyAutomationRules) return 0;
    const matchingRules = surveyData.surveyAutomationRules.filter(rule => {
      const ruleQuestionIds = Array.isArray(rule.questionId) ? rule.questionId : [rule.questionId];
      return ruleQuestionIds.includes(questionId);
    });

    // Debug log
    if (questionId.includes('29')) {
      console.log(`Automation for ${questionId}:`, matchingRules.length, 'rules found');
    }

    return matchingRules.length;
  };

  // Handle View Details button click
  const handleViewDetails = (questionId) => {
    setHighlightedQuestionId(questionId);
    // Auto-scroll to automation section
    setTimeout(() => {
      const automationSection = document.querySelector('.automation-section');
      if (automationSection) {
        automationSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (loading) {
    return (
      <div className={classes.root}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          Loading survey specification...
        </div>
      </div>
    );
  }

  if (!surveyData) {
    return (
      <div className={classes.root}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#ef4444' }}>
          No survey data available
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.surveyTitle}>
            {metadata.title}
          </Typography>
          <Typography className={classes.surveyMeta}>
            Version {metadata.version} • {metadata.totalQuestions} Questions • {metadata.estimatedTime}
          </Typography>
        </Box>
        <Box className={classes.controls}>
          <div className={classes.viewToggle}>
            <ButtonGroup size="small" variant="contained">
              <Button
                onClick={() => setCurrentView('table')}
                color={currentView === 'table' ? 'primary' : 'default'}
                startIcon={<ViewList />}
              >
                Table
              </Button>
              <Button
                onClick={() => setCurrentView('flow')}
                color={currentView === 'flow' ? 'primary' : 'default'}
                startIcon={<AccountTree />}
              >
                Flow
              </Button>
            </ButtonGroup>
          </div>
        </Box>
      </Box>

      {/* Content Area */}
      <Box className={classes.contentArea}>
        {currentView === 'table' ? (
          <div className={classes.tableContainer}>
            {/* Main Survey Table */}
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Question</th>
                  <th>Options</th>
                  <th>Show If</th>
                  <th>Automation</th>
                </tr>
              </thead>
              <tbody>
                {/* Intro Card row (if exists) */}
                {surveyData.introCard && (
                  <tr>
                    <td></td>
                    <td>
                      <Chip
                        label="intro_card"
                        size="small"
                        className={classes.typeChip}
                      />
                    </td>
                    <td className={classes.questionCell}>
                      <div style={{
                        fontWeight: '500',
                        fontSize: '14px',
                      }}>
                        <div style={{ marginBottom: '8px', fontWeight: '600' }}>
                          {surveyData.introCard.title}
                        </div>
                        <div style={{ lineHeight: '1.4', whiteSpace: 'pre-line' }}>
                          {surveyData.introCard.text}
                        </div>
                      </div>
                    </td>
                    <td className={classes.optionsCell}></td>
                    <td className={classes.showIfCell}></td>
                    <td className={classes.automationCell}></td>
                  </tr>
                )}

                {tableData.map((question, index) => {
                  const isHighlighted = highlightedQuestionId === question.id;
                  const automationCount = getAutomationCount(question.id);
                  const showIfText = getShowIfText(question);

                  // Logic for Options column (exactly like original)
                  const isFirstGridItemInGroup = question.type === 'grid_item' &&
                    (index === 0 || tableData[index - 1].parentGrid !== question.parentGrid);

                  let gridItemCount = 1;
                  if (question.type === 'grid_item') {
                    gridItemCount = tableData.filter(q => q.parentGrid === question.parentGrid).length;
                  }

                  const shouldShowOptionsCell = (question.type !== 'grid_item' || isFirstGridItemInGroup);
                  const shouldShowOptionsContent = question.type !== 'group' &&
                                                !(question.type === 'grid' && !question.parentQuestion);

                  return (
                    <tr
                      key={question.id}
                      className={`
                        ${question.type === 'group' ? classes.groupRow : ''}
                        ${question.type === 'grid' ? classes.gridRow : ''}
                        ${isHighlighted ? classes.highlightedRow : ''}
                      `}
                    >
                      <td style={{
                        backgroundColor: question.type === 'group' ? '#f1f5f9' : undefined,
                        padding: question.type === 'group' ? '16px 12px' : undefined,
                      }}>{question.number}</td>

                      <td style={{
                        backgroundColor: question.type === 'group' ? '#f1f5f9' : undefined,
                        padding: question.type === 'group' ? '16px 12px' : undefined,
                      }}>
                        <Chip
                          label={question.type}
                          size="small"
                          className={classes.typeChip}
                        />
                      </td>

                      <td className={classes.questionCell} style={{
                        backgroundColor: question.type === 'group' ? '#f1f5f9' : undefined,
                        padding: question.type === 'group' ? '16px 12px' : undefined,
                      }}>
                        <div style={{
                          paddingLeft: question.type === 'grid_item' ? '36px' : question.type === 'sub_question' ? '36px' : '0px',
                          position: 'relative',
                          fontWeight: question.type === 'grid_item' ? '400' : question.type === 'group' ? '600' : question.type === 'sub_question' ? '400' : '500',
                          fontSize: question.type === 'group' ? '16px' : question.type === 'sub_question' ? '13px' : '14px',
                          color: question.type === 'group' ? '#1e293b' : question.type === 'sub_question' ? '#4b5563' : undefined
                        }}>
                          {(question.type === 'grid_item' || question.type === 'sub_question') && (
                            <span style={{
                              position: 'absolute',
                              left: '12px',
                              color: '#94a3b8',
                              fontSize: '12px'
                            }}>
                              └
                            </span>
                          )}
                          {question.text}
                          {question.type === 'group' && question.sectionText && (
                            <div style={{
                              fontSize: '12px',
                              color: '#64748b',
                              marginTop: '4px',
                              fontStyle: 'italic',
                              fontWeight: '400'
                            }}>
                              {question.sectionText}
                            </div>
                          )}
                          {question.type === 'group' && question.estimatedTime && (
                            <div style={{
                              fontSize: '11px',
                              color: '#94a3b8',
                              marginTop: '2px',
                              fontWeight: '400'
                            }}>
                              (Estimated Time to Complete {question.estimatedTime})
                            </div>
                          )}
                        </div>
                      </td>

                      {shouldShowOptionsCell ? (
                        <td
                          className={classes.optionsCell}
                          rowSpan={question.type === 'grid_item' ? gridItemCount : 1}
                          style={{
                            verticalAlign: 'middle',
                            backgroundColor: question.type === 'group' ? '#f1f5f9' : undefined,
                            border: question.type === 'grid_item' ? '1px solid #e5e7eb' : undefined,
                            padding: question.type === 'group' ? '16px 12px' : undefined,
                          }}
                        >
                          {shouldShowOptionsContent && question.options && (
                            <div>
                              {question.options.map((option) => (
                                <div key={option.value} style={{ margin: '2px 0', fontSize: '0.85rem' }}>
                                  <code style={{
                                    background: '#f1f5f9',
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    marginRight: '6px'
                                  }}>
                                    {option.value}
                                  </code>
                                  <strong>{option.label}</strong>
                                  {option.numericValue !== null && option.numericValue !== undefined && (
                                    <span style={{
                                      marginLeft: '6px',
                                      color: '#166534',
                                      fontWeight: 'bold',
                                      fontSize: '0.8rem'
                                    }}>
                                      ({option.numericValue})
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      ) : null}

                      <td className={classes.showIfCell} style={{
                        backgroundColor: question.type === 'group' ? '#f1f5f9' : undefined,
                        padding: question.type === 'group' ? '16px 12px' : undefined,
                      }}>
                        {(() => {
                          if (showIfText && showIfText !== "Per group Q21 condition" && showIfText !== "Per group Q28 condition") {
                            // Has actual condition - show colored expression
                            return renderShowIfExpression(showIfText);
                          } else if (showIfText === "Per group Q21 condition" || showIfText === "Per group Q28 condition") {
                            // Child of conditional group - show italic text
                            return (
                              <span style={{
                                color: '#6b7280',
                                fontStyle: 'italic',
                                fontSize: '11px',
                                lineHeight: '1.3'
                              }}>
                                {showIfText}
                              </span>
                            );
                          } else {
                            // Always shown - show muted text
                            return (
                              <span style={{
                                color: '#9ca3af',
                                fontStyle: 'italic',
                                fontSize: '11px'
                              }}>
                                Always shown
                              </span>
                            );
                          }
                        })()}
                      </td>

                      <td className={classes.automationCell} style={{
                        backgroundColor: question.type === 'group' ? '#f1f5f9' : undefined,
                        padding: question.type === 'group' ? '16px 12px' : undefined,
                      }}>
                        {automationCount > 0 && (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            className={classes.viewDetailsButton}
                            onClick={() => handleViewDetails(question.id)}
                          >
                            View Details
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

      {/* Automation Rules Table */}
      {surveyData.surveyAutomationRules && surveyData.surveyAutomationRules.length > 0 && (
        <>
          <div className="automation-section" style={{ marginTop: '32px', marginBottom: '16px' }}>
            <Typography variant="h6" style={{ color: '#1e293b', marginBottom: '8px' }}>
              Survey Automation Rules
            </Typography>
            <Typography variant="body2" style={{ color: '#64748b', marginBottom: '16px' }}>
              Detailed automation rules for resource delivery based on survey responses
            </Typography>
          </div>

          <table className={classes.table}>
            <thead>
              <tr>
                <th>Question</th>
                <th>Condition</th>
                <th>Resource/Action</th>
                <th>Schedule</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {surveyData.surveyAutomationRules.map((rule) => (
            rule.actions.map((action, actionIndex) => {
              const isCurrentHighlighted = rule.questionId === highlightedQuestionId
                || (Array.isArray(rule.questionId) && rule.questionId.includes(highlightedQuestionId));

              const isHighlighted = highlightedQuestionId && isCurrentHighlighted;

              return (
              <tr key={`${rule.id}-${actionIndex}`}
                  className={isHighlighted ? classes.automationHighlighted : ''}
                  style={{
                    backgroundColor: isHighlighted ? '#fef3c7' : 'transparent',
                  }}>
                <td style={{
                  color: '#2563eb',
                  fontWeight: 'bold',
                  fontSize: '0.9rem'
                }}>
                  {Array.isArray(rule.questionId) ? rule.questionId.join(', ') : rule.questionId}
                </td>

                <td style={{
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  maxWidth: '150px',
                  wordBreak: 'break-word'
                }}>
                  {renderShowIfExpression(rule.condition)}
                </td>

                <td style={{
                  fontWeight: '500',
                  maxWidth: '180px'
                }}>
                  <div>
                    {action.resource}
                    {action.technicalKey && (
                      <div style={{
                        fontSize: '10px',
                        color: '#64748b',
                        fontFamily: 'monospace',
                        marginTop: '2px'
                      }}>
                        ({action.technicalKey})
                      </div>
                    )}
                  </div>
                </td>

                <td style={{
                  color: '#059669',
                  fontWeight: '500',
                  fontSize: '0.85rem'
                }}>
                  {action.schedule}
                </td>

                <td style={{
                  fontSize: '0.8rem',
                  color: '#64748b',
                  maxWidth: '250px',
                  lineHeight: '1.4'
                }}>
                  {action.notes || action.description}
                </td>
              </tr>
              );
            })
              ))}
            </tbody>
          </table>
        </>
      )}
          </div>
        ) : (
          <div className={classes.flowContainer}>
            <SurveyFlow surveyData={surveyData} />
          </div>
        )}
      </Box>
    </div>
  );
};

export default SurveySpecificationPage;
